import React, { useState, useMemo } from 'react';
import { useCard } from '../../themes/cardStyles';
import { buildPaletteTheme } from '../../themes/palettes';
import { CampaignRune } from '../primitives/CampaignRune';
import { DemoButton } from '../primitives/buttons';

export const CampaignHeroStrip = ({ theme, density, campaign, extra, palette, cardStyle, cardFinish, onEnterAsDm, onOpenSettings, onCreateInvite }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};
  const [hovered, setHovered] = useState(false);
  const isDemoCampaign = !!campaign?.isDemo;
  const canEnterAsDm = campaign?.role === 'owner' || campaign?.role === 'dm';

  const tintedTheme = useMemo(
    () => buildPaletteTheme(theme, palette, cardStyle, cardFinish),
    [theme, palette, cardStyle, cardFinish]
  );
  const cardObj = useCard(tintedTheme, cardStyle, hovered);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...cardObj,
        padding: density.spacing.cardPadding,
        paddingLeft: density.spacing.cardPadding + 8,
        marginBottom: density.spacing.cardGap,
        position: 'relative',
        overflow: 'hidden',
        // Restrained palette identity: hairline outline. The visible
        // gradient comes from the overlay div below; this gives a much
        // more present palette wash than an inset boxShadow could.
        border: `1px solid ${palette.color}40`,
        // On hover, drop any extra layered shadows — only the standard
        // hover lift from cardObj plays, matching the campaign cards'
        // hover effect on the home view.
        transition: e.transition,
      }}
    >
      {/* Palette gradient overlay — visible color wash across the panel
          surface. Diagonal from palette tint at top-left to transparent
          toward the bottom-right. Fades out on hover so the standard
          hover shadow can take over without blur accumulation. */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(135deg, ${palette.color}38 0%, ${palette.color}1a 40%, transparent 75%)`,
        opacity: hovered ? 0 : 1,
        transition: 'opacity 220ms ease',
        pointerEvents: 'none',
        zIndex: 0,
      }} />
      {/* Left-edge palette stripe — the hero's anchor signal. Matches the
          CampaignCard's stripe so the visual language is consistent. */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: 4,
        background: `linear-gradient(180deg, ${palette.light} 0%, ${palette.color} 50%, ${palette.color}aa 100%)`,
      }} />

      {/* Soft top-edge palette accent — keeps the page's top anchor line. */}
      <div style={{
        position: 'absolute', top: 0, left: 4, right: 0, height: 2,
        background: `linear-gradient(90deg, ${palette.color}, transparent 70%)`,
        opacity: 0.6,
      }} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: '48px 1fr auto',
        gap: 18, alignItems: 'flex-start',
      }}>
        {/* Campaign rune — stands on its own without a tile wrapper, so
            the seal reads as an arcane object placed alongside the title
            rather than a contained badge. Palette identity comes from the
            color tint + glow; shadow gives it weight against the page. */}
        <CampaignRune
          name={campaign.rune}
          size={48}
          color={palette.light}
          glow={1}
          shadow={1}
          style={{ flex: '0 0 48px' }}
        />

        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: t.headingFamily,
            fontSize: '1.7rem', fontWeight: 600,
            color: palette.light,
            lineHeight: 1.15,
            textShadow: `${e.headingTextShadow || ''}, 0 0 22px rgba(0,0,0,0.55)`,
          }}>{campaign.name}</div>
          {(campaign.dm || campaign.players > 0 || campaign.schedule) && (
            <div style={{
              fontSize: '0.86rem',
              color: c.textSecondary,
              marginTop: 4,
              fontFamily: t.fontFamily,
            }}>
              {campaign.dm && (
                <>Run by <span style={{ color: c.textPrimary, fontWeight: 600 }}>{campaign.dm}</span></>
              )}
              {(campaign.players > 0 || campaign.schedule) && (
                <span style={{ color: c.textMuted }}>
                  {campaign.dm ? ' · ' : ''}
                  {campaign.players > 0 ? `${campaign.players} at the table` : ''}
                  {campaign.players > 0 && campaign.schedule ? ' · ' : ''}
                  {campaign.schedule || ''}
                </span>
              )}
            </div>
          )}
          {campaign.blurb && (
            <p style={{
              margin: '8px 0 0',
              fontSize: '0.88rem',
              color: c.textSecondary,
              fontStyle: 'italic',
              fontFamily: t.fontFamily,
              maxWidth: 640, lineHeight: 1.5,
              opacity: 0.85,
            }}>{campaign.blurb}</p>
          )}
          {campaign.description && (
            <p style={{
              margin: '6px 0 0',
              fontSize: '0.84rem',
              color: c.textSecondary,
              fontFamily: t.fontFamily,
              maxWidth: 640, lineHeight: 1.55,
              opacity: 0.78,
              whiteSpace: 'pre-wrap',
            }}>{campaign.description}</p>
          )}
        </div>

        {/* Right meta column — Day count, invite code pill, then the
            invite link button. Matches Modern's right-side info pattern
            while keeping Ornate's stronger CTA chrome. */}
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'flex-end', gap: 8,
        }}>
          <span style={{
            fontFamily: t.fontFamily,
            fontSize: '0.7rem',
            color: c.textMuted,
            letterSpacing: '0.02em',
          }}>Day {extra.day || 1}</span>
          {extra.invite && (
            <code style={{
              fontFamily: t.fontFamily,
              fontSize: '0.72rem',
              color: c.textSecondary,
              padding: '3px 8px',
              background: 'rgba(0,0,0,0.32)',
              border: `1px solid ${palette.color}44`,
              borderRadius: 4,
              letterSpacing: '0.02em',
            }}>{extra.invite}</code>
          )}
          <div style={{ marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!isDemoCampaign && canEnterAsDm && onCreateInvite && (
              <span onClick={onCreateInvite}>
              <DemoButton theme={tintedTheme} icon="*" variant="outline">Create invite</DemoButton>
              </span>
            )}
            {canEnterAsDm && (
              <>
                {!isDemoCampaign && onOpenSettings && (
                  <DemoButton theme={tintedTheme} icon="⚙" variant="outline" onClick={onOpenSettings}>Settings</DemoButton>
                )}
                <DemoButton theme={tintedTheme} icon="👑" onClick={onEnterAsDm}>Enter as DM</DemoButton>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
